import { useEffect, useState } from 'react';
import { apiRequest } from '../../api/client';
import './PresetBar.css';

type Preferences = { providerIds: number[]; preferredGenreIds: number[]; excludedGenreIds: number[]; mood: string; maxRuntime: number | null; allowRewatch: boolean };
const slug = (name: string) => `${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 30) || 'preset'}-${Date.now().toString(36).slice(-4)}`;
const same = (a: Preferences, b: Preferences) => JSON.stringify(normalise(a)) === JSON.stringify(normalise(b));
const normalise = (p: Preferences) => ({ ...p, providerIds: [...p.providerIds].sort(), preferredGenreIds: [...p.preferredGenreIds].sort(), excludedGenreIds: [...p.excludedGenreIds].sort(), maxRuntime: p.maxRuntime ?? null, allowRewatch: Boolean(p.allowRewatch) });

// Saved answers for the questionnaire: one tap to fill it in, or save what's there for next time.
const PresetBar = ({ value, onApply }: { value: Preferences; onApply: (preferences: Preferences) => void }) => {
  const [presets, setPresets] = useState<any[]>([]);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [managing, setManaging] = useState(false);
  useEffect(() => { apiRequest<any>('/presets').then(data => setPresets(data.items || [])).catch(() => undefined); }, []);

  const save = async () => {
    if (!name.trim()) return;
    try {
      const saved = await apiRequest<any>(`/presets/${slug(name)}`, { method: 'PUT', body: JSON.stringify({ name: name.trim(), preferences: value }) });
      setPresets(current => [...current, saved]); setNaming(false); setName(''); setMessage(`Saved “${saved.name}”.`);
    } catch (error: any) { setMessage(error.message); }
  };
  const remove = async (preset: any) => {
    await apiRequest(`/presets/${preset.presetId}`, { method: 'DELETE' }).catch(() => undefined);
    setPresets(current => current.filter(item => item.presetId !== preset.presetId));
  };
  const active = presets.find(preset => same(preset.preferences, value));

  return <div className="preset-bar">
    <div className="preset-bar__row">
      <span className="preset-bar__label">⚡ Quick fill</span>
      {presets.map(preset => <span key={preset.presetId} className={`preset-chip${active?.presetId === preset.presetId ? ' is-active' : ''}`}>
        <button type="button" onClick={() => { onApply({ ...value, ...preset.preferences }); setMessage(`Filled in from “${preset.name}”. Tweak anything you like.`); }}>{active?.presetId === preset.presetId ? '✓ ' : ''}{preset.name}</button>
        {managing && <button type="button" className="preset-chip__remove" onClick={() => remove(preset)} aria-label={`Delete ${preset.name}`}>×</button>}
      </span>)}
      {!naming && !active && <button type="button" className="preset-bar__save" onClick={() => { setNaming(true); setMessage(''); }}>+ Save these answers</button>}
      {presets.length > 0 && <button type="button" className="preset-bar__manage" onClick={() => setManaging(value => !value)}>{managing ? 'Done' : 'Edit'}</button>}
    </div>
    {naming && <form className="preset-bar__name" onSubmit={event => { event.preventDefault(); save(); }}>
      <input autoFocus value={name} onChange={event => setName(event.target.value)} placeholder="Name it, e.g. Date night or Friday chill" maxLength={40} aria-label="Preset name" />
      <button disabled={!name.trim()}>Save preset</button><button type="button" onClick={() => setNaming(false)}>Cancel</button>
    </form>}
    {!presets.length && !naming && <p className="preset-bar__hint">Save your usual answers as a preset to fill this in with one tap next time.</p>}
    {message && <p className="preset-bar__hint" aria-live="polite">{message}</p>}
  </div>;
};

export default PresetBar;
